/* istanbul ignore file */
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import { useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import useWayFlyer from '@/common/hooks/useWayFlyer/useWayFlyer'
import configurationServices from '@/configuration/services/services'
import ProductsCard from '@/metrics/components/ProductsCard/ProductsCard'
import ReturnSalesRatioCard from '@/metrics/components/ReturnSalesRatioCard/ReturnSalesRatioCard'
import ReturnsAndExchangesRatioCard from '@/metrics/components/ReturnsAndExchangesRatioCard/ReturnsAndExchangesRatioCard'
import { getReasonsFilters } from '@/metrics/helpers/helpers'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import CountriesMetricsCards from './CountriesMetricsCards/CountriesMetricsCards'
import DeliveryMethodsCard from './DeliveryMethodsCard/DeliveryMethodsCard'
import GeneralMetricsCard from './GeneralMetricsCard/GeneralMetricsCard'
import ReasonsBreakdownCard from './ReasonsBreakdownCard/ReasonsBreakdownCard'
import RefundMethodsCard from './RefundMethodsCard/RefundMethodsCard'
import StoresMetricsCard from './StoreMetricsCard/StoreMetricsCard'
import { productsDescriptions } from './constants'
import { getTopCountriesFiltersByTimeRange } from './helpers'
import * as Styles from './styles'

const ReturnsMetricsContent = ({ isUnifiedDashboard = false, regions, filters }) => {
  const navigation = useNavigate()
  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    setProductsFilters,
    refundMethodsFilters,
    setRefundMethodsFilters,
    topCountriesFilters,
    setTopCountriesFilters,
    storesFilters,
    setStoresFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
    reasonsFilters,
    setReasonsFilters,
  } = filters

  const { data: logisticsPreferences, loading: gettingLogisticsPreferences } = useFetch(
    () =>
      configurationServices.getStoreLogisticsPreferences({
        returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
        withDeleted: true,
      }),
    {
      doInitialRequest: true,
    }
  )
  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const allowExchanges = returnPortalFeatures?.allows_exchanges
  const allowInstantExchanges = returnPortalFeatures?.allows_instant_exchanges
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated
  const giftCardAllowed = returnPortalFeatures?.gift_card_allowed
  const storeCreditAllowed = returnPortalFeatures?.store_credit_allowed
  const { data: generalMetrics, loading: fetchingGeneralMetrics } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getGeneralMetrics,
    filters: commonFilters,
  })

  const {
    data: returnsAndExchangesRatioData,
    loading: loadingReturnsAndExchangesRatio,
    exportData: exportReturnsAndExchangesRatio,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getReturnsAndExchangesRatio,
    filters: commonFilters,
    additionalFilters: returnsAndExchangesRatioFilters,
  })

  const showExchanges = allowExchanges || allowInstantExchanges || isUnifiedDashboard

  const reasonsTableFilters = getReasonsFilters(reasonsFilters, showExchanges)

  const {
    data: reasons,
    loading: loadingReasons,
    exportData: exportReasons,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReasons,
    filters: commonFilters,
    additionalFilters: reasonsTableFilters,
  })

  const {
    data: deliveryMethods,
    loading: loadingDeliveryMethods,
    exportData: exportDeliveryMethods,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getDeliveryMethodsMetrics,
    filters: commonFilters,
  })

  const {
    data: topReturnedProducts,
    loading: loadingTopReturnedProducts,
    exportData: exportTopReturnedProducts,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    filters: commonFilters,
    additionalFilters: productsFilters,
  })

  const {
    data: refundMethodsData,
    loading: fetchingRefundMethodsMetrics,
    exportData: exportRefundMethods,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getRefundMethodsMetrics,
    filters: commonFilters,
    additionalFilters: refundMethodsFilters,
  })

  const { data: returnSalesRatioData, loading: fetchingReturnSalesRatioMetrics } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getReturnSalesRatio,
    filters: commonFilters,
    additionalFilters: returnSalesRatioFilters,
    doRequest: salesOrdersDataActivated || isUnifiedDashboard,
  })

  const {
    data: topCountries,
    loading: fetchingTopCountries,
    exportData: exportTopCountries,
  } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopCountries,
    filters: getTopCountriesFiltersByTimeRange(commonFilters.timeRange, commonFilters),
    additionalFilters: topCountriesFilters,
  })

  const { data: allStoresReturnSalesRatio, loading: fetchingAllStoresReturnSalesRatio } = useMetrics({
    returnPortalId: null,
    service: metricsServices.getAllStoresReturnSalesRatio,
    filters: commonFilters,
    additionalFilters: storesFilters,
    doRequest: isUnifiedDashboard,
  })

  const handleOnClickProduct = productPosition => {
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardProductMetricsDetailPage
      : routes.productMetricsDetailPage

    navigation({
      pathname: pathName.replace(':position', productPosition),
      search: getUrlSearchParams(),
    })
  }

  const handleOnClickVariant = variantPosition => {
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardVariantMetricsDetailPage
      : routes.variantMetricsDetailPage

    navigation({
      pathname: pathName.replace(':variantPosition', variantPosition),
      search: getUrlSearchParams(),
    })
  }

  const handleExportReasons = format => exportReasons(format, 'reasons_total_')

  const handleClickReason = reasonId => {
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardReasonMetricsDetailPage
      : routes.reasonMetricsDetailPage

    navigation({
      pathname: pathName.replace(':reasonKey', reasonId),
      search: getUrlSearchParams(),
    })
  }

  const handleOnClickCategory = categoryPosition => {
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardCategoryMetricsDetailPage
      : routes.categoryMetricsDetailPage

    navigation({
      pathname: pathName.replace(':categoryPosition', categoryPosition),
      search: getUrlSearchParams(),
    })
  }

  const handleOnClickType = typePosition => {
    const pathName = isUnifiedDashboard ? routes.merchantDashboardTypeMetricsDetailPage : routes.typeMetricsDetailPage

    navigation({
      pathname: pathName.replace(':typePosition', typePosition),
      search: getUrlSearchParams(),
    })
  }

  const handleOnClickCollection = collectionPosition => {
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardCollectionMetricsDetailPage
      : routes.collectionMetricsDetailPage

    navigation({
      pathname: pathName.replace(':collectionPosition', collectionPosition),
      search: getUrlSearchParams(),
    })
  }

  const handleOnClickCountry = countryCode => {
    const selectedRegionId = regions?.find(region => region.code === countryCode)?.id

    setCommonFilters({
      ...commonFilters,
      regionId: selectedRegionId,
    })
  }

  const productsDescription = salesOrdersDataActivated
    ? productsDescriptions.withSalesData
    : productsDescriptions.withoutSalesData

  const getSelectedCountry = () => regions?.find(region => region?.id === commonFilters?.regionId)?.code

  useWayFlyer({ language: i18n.language })

  return (
    <>
      <div id="wayflyer-cta-container"></div>
      <Styles.Container>
        <Styles.ColumnsContainer templateColumns="1fr">
          <GeneralMetricsCard
            metrics={generalMetrics}
            loading={fetchingGeneralMetrics}
            currency={info?.selectedStore?.currency}
            giftCardAllowed={giftCardAllowed}
            storeCreditAllowed={storeCreditAllowed}
            isUnifiedDashboard={isUnifiedDashboard}
          />
        </Styles.ColumnsContainer>
        <Styles.ColumnsContainer templateColumns={isUnifiedDashboard ? 'repeat(2, minmax(0, 1fr))' : '1fr'}>
          {isUnifiedDashboard && (
            <StoresMetricsCard
              stores={allStoresReturnSalesRatio?.results || []}
              loading={fetchingAllStoresReturnSalesRatio}
              tableFilters={storesFilters}
              onChangeTableFilters={setStoresFilters}
              salesOrdersDataActivated={salesOrdersDataActivated}
            />
          )}
          <CountriesMetricsCards
            countries={topCountries?.results}
            loading={fetchingTopCountries}
            currency={info?.selectedStore?.currency}
            filters={commonFilters}
            onClickCountry={handleOnClickCountry}
            salesOrdersDataActivated={salesOrdersDataActivated}
            onExport={format => exportTopCountries(format, 'returns_countries_total_')}
            tableFilters={topCountriesFilters}
            onChangeTableFilters={setTopCountriesFilters}
            selectedCountry={getSelectedCountry()}
          />
        </Styles.ColumnsContainer>
        <ReturnSalesRatioCard
          filters={{ ...commonFilters, ...returnSalesRatioFilters }}
          data={returnSalesRatioData}
          loading={fetchingReturnSalesRatioMetrics}
          salesOrdersDataActivated={salesOrdersDataActivated}
          setChartFilters={setReturnSalesRatioFilters}
          showDescription
        />
        <ReturnsAndExchangesRatioCard
          data={returnsAndExchangesRatioData}
          loading={loadingReturnsAndExchangesRatio}
          filters={{ ...commonFilters, ...returnsAndExchangesRatioFilters }}
          onExport={format => exportReturnsAndExchangesRatio(format, 'returns_exchanges_ratio_')}
          setChartFilters={setReturnsAndExchangesRatioFilters}
          description={t(
            'Qué parte de lo devuelto terminó en reembolso y qué parte en cambio, contado desde la fecha en que se completó la devolución.'
          )}
        />
        <Styles.ColumnsContainer templateColumns="1fr 1fr">
          <ReasonsBreakdownCard
            reasons={reasons?.results}
            loading={loadingReasons}
            showExchanges={showExchanges}
            onExport={handleExportReasons}
            tableFilters={reasonsTableFilters}
            onChangeTableFilters={setReasonsFilters}
            onClickReason={handleClickReason}
          />
          <DeliveryMethodsCard
            logisticsPreferences={logisticsPreferences}
            deliveryMethods={deliveryMethods?.results || []}
            carbonOffset={deliveryMethods?.carbon_offset_count}
            loading={loadingDeliveryMethods || gettingLogisticsPreferences}
            onExport={format => exportDeliveryMethods(format, 'returns_methods_total_')}
            isUnifiedDashboard={isUnifiedDashboard}
          />
        </Styles.ColumnsContainer>
        <ProductsCard
          Icon={TShirtIcon}
          title={t('Productos')}
          description={t(productsDescription)}
          loading={loadingTopReturnedProducts}
          products={topReturnedProducts?.results}
          showGroupBySelector={true}
          showExport={true}
          onExport={format => exportTopReturnedProducts(format, 'returned_products_total_')}
          sizeRecommendationsCount={topReturnedProducts?.size_recommendations_count}
          provider={info?.selectedStore?.provider}
          productsFilters={productsFilters}
          onChangeProductsFilters={setProductsFilters}
          onClickProduct={handleOnClickProduct}
          onClickVariant={handleOnClickVariant}
          onClickCategory={handleOnClickCategory}
          onClickType={handleOnClickType}
          onClickCollection={handleOnClickCollection}
          salesOrdersDataActivated={salesOrdersDataActivated}
          carbonOffset={deliveryMethods?.carbon_offset_count}
          isUnifiedDashboard={isUnifiedDashboard}
        />
        <RefundMethodsCard
          totalRefunds={refundMethodsData?.total_count}
          totalAmount={refundMethodsData?.total_amount}
          numOfAvailableRefundMethods={refundMethodsData?.results?.length}
          refundMethods={refundMethodsData?.results || []}
          loading={fetchingRefundMethodsMetrics}
          currency={info?.selectedStore?.currency}
          onExport={format => exportRefundMethods(format, 'refund_methods_total_')}
          refundMethodsFilters={refundMethodsFilters}
          onChangeRefundMethodsFilters={setRefundMethodsFilters}
        />
      </Styles.Container>
    </>
  )
}

export default ReturnsMetricsContent
